import IMask from 'imask';
import moment from 'moment';

import { DateFormat, DateFormatterOptions } from './date-types';

export const ISO_DATE_FORMAT = 'YYYY-MM-DD';
export const ISO_YEAR_MONTH_FORMAT = 'YYYY-MM';
export const ISO_YEAR_FORMAT = 'YYYY';
export const ISO_TIME_FORMAT = 'HH:mm:ss[Z]';

export const DEFAULT_DATE_FORMAT = { format: 'L', parseFormat: ISO_DATE_FORMAT };

export const DATE_FORMATS: Record<string, DateFormat> = {
  full: { format: 'LLLL', parseFormat: ISO_DATE_FORMAT },
  fullDate: { format: 'dddd, LL', parseFormat: ISO_DATE_FORMAT },
  longDate: { format: 'LL', parseFormat: ISO_DATE_FORMAT },
  medium: { format: 'll LTS', parseFormat: moment.defaultFormatUtc },
  mediumDate: { format: 'DD MMM YYYY', parseFormat: moment.defaultFormatUtc },
  mediumTime: { format: 'LTS', parseFormat: moment.defaultFormatUtc },
  short: { format: 'L LT', parseFormat: moment.defaultFormatUtc },
  shortDate: { format: 'L', parseFormat: ISO_DATE_FORMAT },
  shortMonth: { format: 'MM/YYYY', parseFormat: ISO_YEAR_MONTH_FORMAT },
  shortTime: { format: 'LT', parseFormat: ISO_TIME_FORMAT },
  shortYear: { format: 'YYYY', parseFormat: ISO_YEAR_FORMAT },
  spinner: { format: 'dddd, MMM D, YYYY', parseFormat: ISO_DATE_FORMAT },
  timeSpinner: { format: 'dddd, MMM D, YYYY LT', parseFormat: moment.defaultFormatUtc },
  timestamp: { format: 'L LTS', parseFormat: moment.defaultFormatUtc },
  datepicker: { format: 'ddd, MMM DD', parseFormat: ISO_DATE_FORMAT },
};

const getMomentBlocks = (localeData: moment.Locale): {} => ({
  YYYY: { mask: IMask.MaskedRange, from: 0, to: 9999 },
  MMMM: { mask: IMask.MaskedEnum, enum: localeData.months() },
  MMM: { mask: IMask.MaskedEnum, enum: localeData.monthsShort() },
  MM: { mask: IMask.MaskedRange, from: 0, to: 99 },
  M: { mask: IMask.MaskedRange, from: 0, to: 99, maxLength: 2 },
  DD: { mask: IMask.MaskedRange, from: 0, to: 99 },
  D: { mask: IMask.MaskedRange, from: 1, to: 99, maxLength: 2 },
  dddd: { mask: IMask.MaskedEnum, enum: localeData.weekdays() },
  ddd: { mask: IMask.MaskedEnum, enum: localeData.weekdaysShort() },
  dd: { mask: IMask.MaskedEnum, enum: localeData.weekdaysMin() },
  d: { mask: IMask.MaskedRange, from: 0, to: 9 },
  HH: { mask: IMask.MaskedRange, from: 0, to: 99 },
  hh: { mask: IMask.MaskedRange, from: 0, to: 99 },
  h: { mask: IMask.MaskedRange, from: 0, to: 99, maxLength: 2 },
  mm: { mask: IMask.MaskedRange, from: 0, to: 99 },
  ss: { mask: IMask.MaskedRange, from: 0, to: 99 },
  A: { mask: IMask.MaskedEnum, enum: ['AM', 'PM'], maxLength: 2 },
});

function replaceLocalized(format: string, localeData: moment.Locale): string {
  return format.split(' ').map((block) => {
    const longDate = localeData.longDateFormat(block as moment.LongDateFormatKey);

    return longDate
      ? longDate.replace(/\[/g, '{').replace(/\]/g, '}')
      : block;
  }).join(' ');
}

function isUTC(value: string): boolean {
  return value.endsWith('Z');
}

function toDate(value: string, parseFormat: string): Date {
  const mDate = isUTC(value)
    ? moment.utc(value, parseFormat)
    : moment(value, parseFormat).utc();

  return mDate.toDate();
}

function isValid(value: string, parseFormat: string = moment.defaultFormatUtc): boolean {
  return moment(value, parseFormat, true).isValid();
}

function isValidDate(date: Date): boolean {
  //@ts-ignore
  return date instanceof Date && !isNaN(date);
}

function getDateFormat(type: string): DateFormat {
  return DATE_FORMATS[type] || DEFAULT_DATE_FORMAT;
}

export function getPattern(format: string): string {
  return replaceLocalized(format, moment.localeData());
}

function createMask(options: DateFormatterOptions): IMask.MaskedDate {
  const { type = 'date' } = options;

  const localeData = moment.localeData();
  const blocks = getMomentBlocks(localeData);
  const { format } = getDateFormat(type);

  const maskOptions: IMask.MaskedDateOptions = {
    blocks,
    format: date => {
      // @ts-ignore
      return isValidDate(date) ? moment(date).format(format) : date.lastFormattedValue;
    },
    lazy: true,
    mask: Date,
    parse: str => {
      const date = moment(str, format).toDate();

      //@ts-ignore
      date.lastFormattedValue = str;

      return date;
    },
    pattern: getPattern(format),
  };

  return IMask.createMask(maskOptions);
}

export {
  createMask,
  getDateFormat,
  getMomentBlocks,
  isUTC,
  isValid,
  replaceLocalized,
  toDate,
};
